import type { DialogueTree } from './types';

/**
 * Reporter. Tone: deadline-brain, casual but ruthless, will quote anything you
 * give them. Every choice has a publication risk. Off-the-record is mostly a
 * fiction.
 */
export const REPORTER_TREE: DialogueTree = {
  contactId: 'reporter',
  rootChoices: [
    {
      id: 'no-comment',
      label: 'tell them "no comment"',
      playerSays: "no comment",
      responses: [
        {
          text: "got it. \"no comment\" runs as: \"declined to comment when reached for this story.\" that is a comment, journalistically speaking. your call.",
          effects: [{ type: 'note', value: 'reporter will run "declined to comment". expect press fallout.' }, { type: 'tribeRep', target: 'press', value: -2 }],
        },
        {
          text: "noted. you\'re the fourth person to text me \"no comment\" today. business is good.",
        },
      ],
    },

    {
      id: 'on-record',
      label: 'go on the record',
      playerSays: "on record. go.",
      effects: [{ type: 'tribeRep', target: 'press', value: 4 }, { type: 'notoriety', value: 6 }, { type: 'note', value: 'going on record with reporter. press rep +4 / notoriety +6' }],
      responses: [
        {
          text: "okay. one minute. recording.\n\nask me your question. i will print exactly what you say. i will print one word more if you let me. choose carefully.",
          followUps: [
            { id: 'record-defend', label: "i did nothing wrong. period.",
              playerSays: "i did nothing wrong. period.",
              effects: [{ type: 'notoriety', value: 4 }, { type: 'tribeRep', target: 'press', value: -2 }, { type: 'note', value: '"did nothing wrong" quote will run. it always runs.' }],
              responses: [
                { text: "running it.\n\n\"i did nothing wrong, period\" — accountholder, in a written statement.\n\nit will, statistically, age poorly. but you said it. on the record." },
              ],
            },
            { id: 'record-explain', label: "let me walk you through what actually happened",
              playerSays: "let me walk you through what actually happened",
              effects: [{ type: 'tribeRep', target: 'press', value: 8 }, { type: 'note', value: '"walk you through" — best move you can make with press. +8 press rep' }],
              responses: [
                { text: "appreciated. genuinely. nobody does this anymore.\n\ni\'ll run the most charitable read of your version. legally allowed to charitable-read. that\'s the deal." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'off-record',
      label: 'go off the record',
      playerSays: "off the record",
      responses: [
        {
          text: "off the record means \"on background.\" on background means \"i can use it but i can\'t say it came from you.\" you\'ll see it in the article. you will not see your name. that\'s the deal.\n\nstill want to talk?",
          followUps: [
            { id: 'off-tell', label: "yes. here\'s the thing.",
              playerSays: "yes. here\'s the thing.",
              effects: [{ type: 'tribeRep', target: 'press', value: 5 }, { type: 'note', value: 'off-the-record source. press rep +5. you\'ll see it in the column' }],
              responses: [
                { text: "got it. you\'ll see the column on tuesday.\n\n\"according to a person familiar with the matter…\" you\'re the person. you are now familiar." },
              ],
            },
            { id: 'off-cancel', label: "actually scratch that.",
              playerSays: "actually scratch that.",
              responses: [
                { text: "understood. i\'ll forget it. for legal definitions of \"forget.\" we move on." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'leak-rival',
      label: 'leak something on a rival trader',
      playerSays: "i have something on a rival. interested?",
      effects: [{ type: 'tribeRep', target: 'press', value: 3 }, { type: 'fbiHeat', value: 4 }, { type: 'note', value: 'leaked dirt on a rival. press rep +3. heat +4.' }],
      responses: [
        {
          text: "always interested.\n\ndump it. i\'ll verify independently. if it checks, you\'re an unnamed source. if it doesn\'t, this conversation didn\'t happen and i\'ll never quote you again.",
        },
      ],
    },

    {
      id: 'rage',
      label: 'rage at the reporter',
      destructive: true,
      playerSays: "you\'re garbage and so is your outlet",
      effects: [{ type: 'tribeRep', target: 'press', value: -10 }, { type: 'note', value: 'raged at reporter. press rep -10. expect retaliatory column' }],
      responses: [
        {
          text: "noted.\n\nhostility is great copy. that exact line is going in the kicker. thanks.",
        },
      ],
    },

    {
      id: 'plant-story',
      label: 'plant a flattering story',
      playerSays: "would you be interested in a feature about my journey",
      responses: [
        { text: "love a journey. love a feature. love a feature about a journey.\n\nour rate for a profile is, technically, free. our rate for a flattering profile is, structurally, a Moncler jacket and access. you have neither. but i\'ll see what i can do.",
          followUps: [
            { id: 'plant-bribe', label: "i\'ll send the jacket and the access",
              playerSays: "the jacket and the access. on the way.",
              effects: [{ type: 'cash', value: -2_500 }, { type: 'tribeRep', target: 'press', value: 8 }, { type: 'notoriety', value: 6 }, { type: 'note', value: '-$2,500 (jacket). press +8 / notoriety +6' }],
              responses: [
                { text: "thank you. the piece will run sunday. it will not be flattering, in your opinion. it will be, in journalism, flattering. there is a difference. you will learn it." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'tip-corruption',
      label: 'tip them about corrupt rival',
      playerSays: "i have receipts on a competing trader's offshore stuff",
      effects: [{ type: 'tribeRep', target: 'press', value: 5 }, { type: 'fbiHeat', value: 3 }, { type: 'note', value: 'tipped reporter on rival. press +5 / FBI +3 (you\'re now in the file too).' }],
      responses: [
        { text: "anonymous source. i\'ll need to see receipts. they need to be real. \"real\" is, journalistically, more demanding than you think.\n\nif it checks, the piece runs in 11 days. you\'ll see it. you\'ll know who it\'s about. nobody else will. that\'s the deal." },
      ],
    },
  ],
};
